/**
 * Les articles à créer, là où l'assistante regarde déjà ce qu'on lui demande de valider.
 *
 * *Demande de Chris, 08/09/2026 : « comment l'assistante sait qu'elle a un article à créer et où
 * va-t-elle pour valider la création dans l'appli ? Je verrais bien un menu dans soumission
 * article en attente où l'assistante aurait juste à bien codifier l'article et rajouter tous les
 * champs nécessaires de la fiche article, et en même temps du coup récupérer la photo s'il y en a
 * une. »*
 *
 * ## Pourquoi ici et plus dans l'écran Stocks
 *
 * La validation vivait dans l'écran Stocks jusqu'au 08/09/2026, sous le formulaire du magasinier.
 * C'était le mauvais endroit pour deux raisons : l'assistante référente n'a rien à faire dans
 * l'écran du magasin, et surtout **elle n'avait aucun moyen d'apprendre qu'une demande
 * l'attendait** sans y passer d'elle-même. Elle vient déjà dans Soumissions pour répondre aux
 * contre-études ; une création d'article est une demande de plus qu'on lui adresse, elle se lit
 * donc au même endroit.
 *
 * L'écran Stocks garde la **demande** et le suivi. Un seul endroit pour demander, un seul pour
 * décider — les garder tous les deux aurait fait deux chemins vers la même écriture.
 *
 * ## Ce que l'assistante saisit, et ce qu'elle ne saisit pas
 *
 * Les champs de la fiche **dont l'application se sert** : le code de l'ERP, la description,
 * l'unité, le prix d'achat, le **coût d'étude**, le prix de vente, la famille, la procédure
 * d'approvisionnement, le fournisseur.
 *
 * Le coût d'étude est le moins évident et le plus important : c'est **lui** qu'une ligne de
 * contre-étude prend quand on y ajoute un article (règle du 21/08/2026), le seul des trois
 * montants qui soit un déboursé. Un article créé sans lui entre à zéro dans un chiffrage. L'écran
 * le prérenseigne donc avec le prix d'achat, qui en est la meilleure approximation.
 *
 * Les vingt colonnes de nomenclature de l'ERP — les quatre niveaux de classification, le centre,
 * l'activité, la nature — ne sont **pas** proposées : elles viennent du réimport du classeur, qui
 * les porte toutes. Les faire saisir à la main reviendrait à recopier l'ERP, et la première faute
 * de frappe ferait diverger deux référentiels.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  chargerDemandesArticles,
  LIBELLES_ORIGINE_DEMANDE,
  refuserDemandeArticle,
  validerDemandeArticle,
} from "./api";
import type { DemandeArticle, TableauDemandesArticles } from "./api";

function formaterDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

/** La fiche en cours de saisie, une par demande ouverte : deux demandes ne se mélangent pas. */
type Fiche = {
  readonly code: string;
  readonly description: string;
  readonly uniteBase: string;
  readonly coutUnitaire: string;
  readonly coutEtude: string;
  readonly prixUnitaire: string;
  readonly codeFamille: string;
  readonly procedureAppro: string;
  readonly fournisseurNom: string;
  readonly motif: string;
};

/** Préremplie avec ce que le demandeur a fourni : elle relit, elle ne resaisit pas. */
function ficheDepuis(demande: DemandeArticle): Fiche {
  return {
    code: "",
    description: demande.description,
    uniteBase: demande.uniteBase ?? "",
    coutUnitaire: "",
    coutEtude: "",
    prixUnitaire: "",
    codeFamille: "",
    procedureAppro: "",
    fournisseurNom: demande.fournisseurNom ?? "",
    motif: "",
  };
}

export function CreationsArticles(): JSX.Element {
  const [tableau, setTableau] = useState<TableauDemandesArticles | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [fiches, setFiches] = useState<Record<string, Fiche>>({});
  const [refusOuvert, setRefusOuvert] = useState<string | null>(null);
  const [voirDecidees, setVoirDecidees] = useState(false);

  const recharger = useCallback((): void => {
    chargerDemandesArticles()
      .then((r) => {
        setTableau(r);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  const ficheDe = (demande: DemandeArticle): Fiche =>
    fiches[demande.id] ?? ficheDepuis(demande);

  const poser = (demande: DemandeArticle, champs: Partial<Fiche>): void => {
    setFiches((avant) => ({ ...avant, [demande.id]: { ...ficheDe(demande), ...champs } }));
  };

  const agir = (id: string, promesse: Promise<unknown>, message: string): void => {
    setOccupe(id);
    setErreur(null);
    setAvis(null);
    promesse
      .then(() => {
        setAvis(message);
        setRefusOuvert(null);
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  const creer = (demande: DemandeArticle): void => {
    const f = ficheDe(demande);
    const vide = (v: string): string | null => (v.trim() === "" ? null : v.trim());
    agir(
      demande.id,
      validerDemandeArticle(demande.id, {
        code: f.code.trim(),
        ...(vide(f.description) === null ? {} : { description: f.description.trim() }),
        uniteBase: vide(f.uniteBase),
        coutUnitaire: vide(f.coutUnitaire),
        // À défaut de saisie, le serveur reprend le prix d'achat : un déboursé vaut mieux qu'un
        // zéro, qui ferait entrer l'article à rien dans un chiffrage.
        coutEtude: vide(f.coutEtude),
        prixUnitaire: vide(f.prixUnitaire),
        codeFamille: vide(f.codeFamille),
        procedureAppro: vide(f.procedureAppro),
        fournisseurNom: vide(f.fournisseurNom),
      }),
      `Article créé sous le code ${f.code.trim()}` +
        (demande.quantite === null ? "." : `, et ${sansZeros(demande.quantite)} entrés en stock.`) +
        (demande.photo === null ? "" : " La photo est passée sur la fiche."),
    );
  };

  const demandes = tableau?.demandes ?? [];
  const aCreer = demandes.filter((d) => d.statut === "en_attente");
  const decidees = demandes.filter((d) => d.statut !== "en_attente");

  // Le serveur tranche qui décide ; l'écran ne fait que suivre. Un bouton masqué n'est pas une
  // garde, et la route revérifie de son côté.
  const jePeuxDecider = tableau?.jePeuxDecider ?? false;

  return (
    <section className="creations-articles">
      <h3>
        Articles à créer
        {aCreer.length > 0 && <span className="creations-articles__compte">{aCreer.length}</span>}
      </h3>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      {avis !== null && <p className="etat">{avis}</p>}

      {tableau !== null && !jePeuxDecider && (
        <p className="etat">
          Vous voyez les demandes, mais c&apos;est{" "}
          {tableau.titulaire === null
            ? "la personne nommée sur la fonction « Validation des créations d'articles »"
            : tableau.titulaire.nom}{" "}
          qui les crée. L&apos;administrateur nomme ce titulaire dans l&apos;écran Membres.
        </p>
      )}

      {tableau === null ? (
        <p className="etat">Chargement…</p>
      ) : aCreer.length === 0 ? (
        <p className="etat">Aucun article à créer.</p>
      ) : (
        <ul className="creations-articles__liste">
          {aCreer.map((d) => (
            <li key={d.id} className="creations-articles__ligne">
              <p className="creations-articles__entete">
                <strong>{d.description}</strong>
                <span>
                  demandé le {formaterDate(d.creeLe)}
                  {d.demandeurNom !== null && ` par ${d.demandeurNom}`}
                </span>
              </p>

              <p className="creations-articles__details">
                {d.quantite !== null && (
                  <span className="creations-articles__quantite">
                    {sansZeros(d.quantite)} {d.uniteBase ?? ""} à entrer en stock
                    {d.origine !== null && ` · ${LIBELLES_ORIGINE_DEMANDE[d.origine]}`}
                  </span>
                )}
                {d.remarque !== null && <span>« {d.remarque} »</span>}
              </p>

              <div className="creations-articles__corps">
                {/* LA PHOTO, en grand : c'est elle qui dit ce que l'objet est, quand le libellé
                    du magasin ne suffit pas à le codifier. Elle passera sur la fiche. */}
                {d.photo !== null && (
                  <figure className="creations-articles__photo">
                    <img src={d.photo} alt={`Photo de ${d.description}`} />
                    <figcaption>Jointe à la fiche à la création</figcaption>
                  </figure>
                )}

                {jePeuxDecider && (
                  <form
                    className="creations-articles__fiche"
                    onSubmit={(e) => {
                      e.preventDefault();
                      creer(d);
                    }}
                  >
                    <label className="creations-articles__code">
                      Code de l&apos;ERP
                      <input
                        value={ficheDe(d).code}
                        placeholder="1009999"
                        onChange={(e) => {
                          poser(d, { code: e.target.value });
                        }}
                      />
                    </label>

                    <label className="creations-articles__large">
                      Description
                      <input
                        value={ficheDe(d).description}
                        onChange={(e) => {
                          poser(d, { description: e.target.value });
                        }}
                      />
                    </label>

                    <label>
                      Unité
                      <input
                        value={ficheDe(d).uniteBase}
                        placeholder="U, ML, T…"
                        onChange={(e) => {
                          poser(d, { uniteBase: e.target.value });
                        }}
                      />
                    </label>

                    <label>
                      Prix d&apos;achat (€)
                      <input
                        inputMode="decimal"
                        value={ficheDe(d).coutUnitaire}
                        onChange={(e) => {
                          // Le coût d'étude suit le prix d'achat tant qu'on ne l'a pas touché :
                          // c'est sa meilleure approximation, et un zéro ferait entrer l'article
                          // à rien dans un chiffrage.
                          const suit =
                            ficheDe(d).coutEtude === "" ||
                            ficheDe(d).coutEtude === ficheDe(d).coutUnitaire;
                          poser(d, {
                            coutUnitaire: e.target.value,
                            ...(suit ? { coutEtude: e.target.value } : {}),
                          });
                        }}
                      />
                      {d.quantite !== null && <small>Il valorise l&apos;entrée en stock.</small>}
                    </label>

                    <label>
                      Coût d&apos;étude (€)
                      <input
                        inputMode="decimal"
                        value={ficheDe(d).coutEtude}
                        onChange={(e) => {
                          poser(d, { coutEtude: e.target.value });
                        }}
                      />
                      <small>Le prix que prendra une contre-étude.</small>
                    </label>

                    <label>
                      Prix de vente (€)
                      <input
                        inputMode="decimal"
                        value={ficheDe(d).prixUnitaire}
                        onChange={(e) => {
                          poser(d, { prixUnitaire: e.target.value });
                        }}
                      />
                    </label>

                    <label>
                      Code famille
                      <input
                        value={ficheDe(d).codeFamille}
                        placeholder="10 10 005 005"
                        onChange={(e) => {
                          poser(d, { codeFamille: e.target.value });
                        }}
                      />
                    </label>

                    <label>
                      Procédure d&apos;appro.
                      <input
                        value={ficheDe(d).procedureAppro}
                        placeholder="Direct"
                        onChange={(e) => {
                          poser(d, { procedureAppro: e.target.value });
                        }}
                      />
                    </label>

                    <label className="creations-articles__large">
                      Fournisseur
                      <input
                        value={ficheDe(d).fournisseurNom}
                        onChange={(e) => {
                          poser(d, { fournisseurNom: e.target.value });
                        }}
                      />
                    </label>

                    <p className="creations-articles__note">
                      La classification de l&apos;ERP — les quatre niveaux, le centre,
                      l&apos;activité — vient du réimport du classeur des articles, pas d&apos;ici.
                    </p>

                    <div className="creations-articles__gestes">
                      <button
                        type="submit"
                        disabled={
                          occupe === d.id ||
                          ficheDe(d).code.trim() === "" ||
                          (d.quantite !== null && ficheDe(d).coutUnitaire.trim() === "")
                        }
                      >
                        {occupe === d.id
                          ? "Création…"
                          : d.quantite === null
                            ? "Créer l'article"
                            : `Créer et entrer ${sansZeros(d.quantite)} en stock`}
                      </button>

                      {/* Refuser est l'exception : son champ ne s'ouvre qu'au clic, pour que le
                          geste attendu reste celui de créer. */}
                      {refusOuvert === d.id ? (
                        <div className="creations-articles__refus">
                          <label>
                            Motif du refus
                            <input
                              value={ficheDe(d).motif}
                              placeholder="Existe déjà sous le code…"
                              autoFocus
                              onChange={(e) => {
                                poser(d, { motif: e.target.value });
                              }}
                            />
                            <small>
                              Le demandeur le lira : sans motif, il refera la même demande.
                            </small>
                          </label>
                          <button
                            type="button"
                            className="bouton-danger"
                            disabled={occupe === d.id || ficheDe(d).motif.trim() === ""}
                            onClick={() => {
                              agir(
                                d.id,
                                refuserDemandeArticle(d.id, ficheDe(d).motif.trim()),
                                "Demande refusée, le demandeur est averti du motif.",
                              );
                            }}
                          >
                            Confirmer le refus
                          </button>
                          <button
                            type="button"
                            className="lien"
                            onClick={() => {
                              setRefusOuvert(null);
                            }}
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="lien"
                          onClick={() => {
                            setRefusOuvert(d.id);
                          }}
                        >
                          Refuser cette demande
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {decidees.length > 0 && (
        <details
          className="creations-articles__decidees"
          open={voirDecidees}
          onToggle={(e) => {
            setVoirDecidees((e.currentTarget as HTMLDetailsElement).open);
          }}
        >
          <summary>Déjà décidé ({decidees.length})</summary>
          <ul>
            {decidees.map((d) => (
              <li key={d.id}>
                <span>{d.description}</span>
                {d.statut === "validee" && d.article !== null ? (
                  <span>
                    créé <strong>{d.article.code}</strong>
                    {d.entreeEnStock !== null &&
                      ` · ${sansZeros(d.entreeEnStock.quantite)} en stock`}
                  </span>
                ) : (
                  <span className="en-ecart">refusé : {d.motifRefus ?? ""}</span>
                )}
                <span className="creations-articles__quand">
                  {d.decideLe === null ? "" : formaterDate(d.decideLe)}
                  {d.decideurNom !== null && ` · ${d.decideurNom}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
